import type {CSSProperties, ReactElement} from 'react';
import {useState, useEffect} from 'react';
import type {PosterChangingOverlayProps} from '@Components/poster-whiteboard/components/poster-changing-overlay/poster-changing-overlay.types';
import style from './poster-changing-overlay.module.scss';
import {PosterModeType} from '@PosterWhiteboard/poster/poster-mode.class';
import {useAppSelector} from '@/hooks';
import type * as Fabric from '@postermywall/fabricjs-2';
import type {Canvas} from '@postermywall/fabricjs-2';

export default function PosterChangingOverlay({...props}: PosterChangingOverlayProps): ReactElement | null {
  const [capturedBackdrop, setCapturedBackdrop] = useState<string | undefined>();

  const pendingDesignBackdropUrl = useAppSelector((state) => {
    return state.posterEditor.pendingDesignBackdropUrl;
  });

  useEffect(() => {
    if (pendingDesignBackdropUrl) {
      return;
    }

    let imageSrc: string | undefined;
    if (props.isPosterLoading) {
      imageSrc = props.posterOpts.mode.details.type !== PosterModeType.CREATE ? props.posterOpts.savedPosterPreviewUrls[0] : undefined;
    } else {
      const canvas = window.posterEditor?.whiteboard?.getCurrentPage().fabricCanvas as Canvas;
      if (!canvas) {
        return;
      }
      imageSrc = canvas.toDataURL({
        format: 'png' as Fabric.ImageFormat,
        enableRetinaScaling: false,
        multiplier: 1,
        quality: 1,
      });
    }

    setCapturedBackdrop(imageSrc);
  }, []);

  const backdropImage = pendingDesignBackdropUrl ?? capturedBackdrop;

  const styles: CSSProperties = {};
  if (props.width) {
    styles.width = `${props.width}px`;
  }
  if (props.height) {
    styles.height = `${props.height}px`;
  }

  const getBackdrop = (): ReactElement => {
    return (
      <div className="_full-width _full-height">
        <img className="_full-width _full-height" alt="Poster" src={backdropImage} />
      </div>
    );
  };

  const getAnimation = (): ReactElement | null => {
    if (pendingDesignBackdropUrl) {
      return null;
    }
    return <div className={`${style.animation} _full-width _full-height`} />;
  };

  return backdropImage ? (
    <div className={props.className ?? ''} style={styles}>
      {getBackdrop()}
      {getAnimation()}
    </div>
  ) : null;
}
